import React, { useRef } from 'react';
import ThemeButton from 'Components/ThemedButton';
import AddressDropdown from 'Components/SearchAddress/AddressDropdown/AddressDropdown';
import styled from 'styled-components';
import { SEARCH_ADDRESS_BTN_TEST_ID, SEARCH_ADDRESS_FORM_TEST_ID } from 'Components/SearchAddress/constants';
import { Box, Flex } from '@igooods/ui-kit';
import SearchAddress, { ISearchAddress } from 'Components/SearchAddress';
import ArrowGeo from 'Components/Icon/ArrowGeo';
import { colors } from 'constants/colors';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentCityId } from 'Modules/SearchAddress/selectors';
import { getCurrentPosition } from 'Modules/SearchAddress/actions';
import { EnumInputSize, EnumButtonSize, EnumTheme, EnumInputTheme } from 'constants/enums';
import Text from 'Components/Typography/Text';
import { FontSize, FontWeight, Transitions, FontFamily } from 'theme';
import { useHistory } from 'react-router';
import { setSAToLocation } from 'Components/landings/LandingSearchAddress/utils';

export const LOCATION_BTN_TEST_ID = 'LOCATION_BTN_TEST_ID';

const LocationBtn = styled(Flex).attrs({
  middle: true,
  center: true,
  children: <ArrowGeo />,
})`
  width: 48px;
  height: 48px;
  cursor: pointer;
  border-radius: 100px;
  &:hover {
    background-color: ${colors.ghostWhiteTwo};
  }
`;

const LocationBtnLanding = styled(LocationBtn)`
  margin-left: -11px;
`;

const MapBtn = styled(Flex).attrs({ px: 3.5, center: true, middle: true })`
  border-radius: 100px;
  height: 48px;
  cursor: pointer;
  transition: ${Transitions.bg};
  &:hover {
    background-color: ${colors.titanWhite};
  }
`;

const Form = styled.form`
  display: flex;
  margin: 0;
`;

export enum SearchAddressSizes {
  big = 'big',
  small = 'small',
}

export enum SearchAddressFromType {
  select_address = 'select_address',
  main = 'main',
}

export interface ISearchAddressDesktop extends Omit<ISearchAddress, 'children'> {
  autoFocus?: boolean;
  size?: SearchAddressSizes;
  fromType?: SearchAddressFromType;
  use?: EnumInputTheme;
}

const sizes = {
  [SearchAddressSizes.big]: {
    inputSize: EnumInputSize.large,
    btnSize: EnumButtonSize.large,
    btnWidth: '202px',
    withError: true,
  },
  [SearchAddressSizes.small]: {
    inputSize: EnumInputSize.normal,
    btnSize: EnumButtonSize.normal,
    btnWidth: '160px',
    withError: false,
  },
};

const SearchAddressDesktop: React.FC<ISearchAddressDesktop> = ({
  onSelectAddress,
  autoFocus = false,
  size = SearchAddressSizes.big,
  fromType = SearchAddressFromType.select_address,
  use = EnumInputTheme.round,
}) => {
  const dispatch = useDispatch();
  const cityId = useSelector(getCurrentCityId);
  const { inputSize, btnSize, btnWidth, withError } = sizes[size];
  const history = useHistory();
  const submitBtnRef = useRef<HTMLButtonElement>(null);

  const handleClickLocation = () => {
    getCurrentPosition(dispatch, cityId);
  };

  const handleClickMapBtn = () => {
    setSAToLocation(history);
  };

  const propertiesByFromType = {
    [SearchAddressFromType.select_address]: {
      after: <LocationBtn data-testid={LOCATION_BTN_TEST_ID} onClick={handleClickLocation} />,
      before: undefined,
      btnText: 'Продолжить',
    },
    [SearchAddressFromType.main]: {
      after: (
        <MapBtn onClick={handleClickMapBtn}>
          <Text
            fontFamily={FontFamily.secondFont}
            size={FontSize.xs}
            color={colors.blackRussian}
            fontWeight={FontWeight.semiBold}
          >
            Карта
          </Text>
        </MapBtn>
      ),
      before: <LocationBtnLanding data-testid={LOCATION_BTN_TEST_ID} onClick={handleClickLocation} />,
      btnText: 'Выбрать магазин',
    },
  };

  const { after, before, btnText } = propertiesByFromType[fromType];

  return (
    <SearchAddress onSelectAddress={onSelectAddress}>
      {({
        handleSubmit,
        handleChangeAddress,
        handleSelectAddress,
        handleUpdateCurrentAddress,
        handleResetErrors,
        addressesList,
        errorMsg,
        enteredAddress,
        isLoading,
      }) => {
        return (
          <Form onSubmit={handleSubmit} data-testid={SEARCH_ADDRESS_FORM_TEST_ID}>
            <Box width="360px" mr={3.75}>
              <AddressDropdown
                onChangeAddress={handleChangeAddress}
                onSelectAddress={
                  fromType === SearchAddressFromType.main ? handleUpdateCurrentAddress : handleSelectAddress
                }
                enteredAddress={enteredAddress}
                list={addressesList}
                errorMsg={withError ? errorMsg : Boolean(errorMsg)}
                size={inputSize}
                {...{ use, after, before, autoFocus, ...(errorMsg && { onFocusInput: handleResetErrors }) }}
                submitBtnRef={submitBtnRef}
              />
            </Box>
            <Box hideBefore="sm" width={btnWidth}>
              <ThemeButton
                size={btnSize}
                use={EnumTheme.roundGreen}
                type="submit"
                isLoading={isLoading}
                testId={SEARCH_ADDRESS_BTN_TEST_ID}
                fluid
                ref={submitBtnRef}
              >
                {btnText}
              </ThemeButton>
            </Box>
            <Box hideAfter="sm" width="63px">
              <ThemeButton
                size={btnSize}
                use={EnumTheme.roundGreen}
                type="submit"
                isLoading={isLoading}
                testId={SEARCH_ADDRESS_BTN_TEST_ID}
                fluid
              >
                ОК
              </ThemeButton>
            </Box>
          </Form>
        );
      }}
    </SearchAddress>
  );
};

export default SearchAddressDesktop;
